// BiometricCaptureModal.jsx - Interface de capture biométrique (empreintes et scan rétinien)
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Fingerprint, Eye, Camera, CheckCircle, XCircle, RefreshCw, 
  AlertTriangle, Loader2, Hand, ScanLine, Zap
} from 'lucide-react';

// Fingers for fingerprint capture
const FINGERS = [
  { id: 'thumb_r', name: { fr: 'Pouce droit', en: 'Right Thumb' } },
  { id: 'index_r', name: { fr: 'Index droit', en: 'Right Index' } },
  { id: 'middle_r', name: { fr: 'Majeur droit', en: 'Right Middle' } },
  { id: 'ring_r', name: { fr: 'Annulaire droit', en: 'Right Ring' } },
  { id: 'pinky_r', name: { fr: 'Auriculaire droit', en: 'Right Pinky' } },
  { id: 'thumb_l', name: { fr: 'Pouce gauche', en: 'Left Thumb' } },
  { id: 'index_l', name: { fr: 'Index gauche', en: 'Left Index' } },
  { id: 'middle_l', name: { fr: 'Majeur gauche', en: 'Left Middle' } },
  { id: 'ring_l', name: { fr: 'Annulaire gauche', en: 'Left Ring' } },
  { id: 'pinky_l', name: { fr: 'Auriculaire gauche', en: 'Left Pinky' } },
];

const BiometricCaptureModal = ({ 
  open, 
  onClose, 
  onCapture, 
  captureType = 'fingerprint', // 'fingerprint' or 'retinal'
  existingData = null 
}) => {
  const { language } = useAuth();
  const [capturing, setCapturing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFinger, setCurrentFinger] = useState(0);
  const [capturedFingers, setCapturedFingers] = useState({});
  const [retinalCaptured, setRetinalCaptured] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'ready', 'error'
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Simulate device connection
  useEffect(() => {
    if (open) {
      setDeviceStatus('connecting');
      const timer = setTimeout(() => {
        // Simulate device detection - in production, this would check for actual hardware
        const hasDevice = Math.random() > 0.3; // 70% chance device is "found"
        if (hasDevice) {
          setDeviceStatus('ready');
          toast.success(
            language === 'fr' 
              ? 'Appareil biométrique détecté' 
              : 'Biometric device detected'
          );
        } else {
          setDeviceStatus('error');
          setError(
            language === 'fr'
              ? 'Aucun appareil biométrique détecté. Veuillez connecter un scanner d\'empreintes ou de rétine.'
              : 'No biometric device detected. Please connect a fingerprint or retinal scanner.'
          );
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [open, language]);

  // Animate scan effect
  useEffect(() => {
    if (capturing && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      let scanY = 0;
      
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw background
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw scan line
        const gradient = ctx.createLinearGradient(0, scanY - 20, 0, scanY + 20);
        gradient.addColorStop(0, 'transparent');
        gradient.addColorStop(0.5, '#22c55e');
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, scanY - 20, canvas.width, 40);
        
        // Draw fingerprint pattern (simplified)
        if (captureType === 'fingerprint') {
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 1;
          for (let i = 0; i < 15; i++) {
            ctx.beginPath();
            ctx.arc(canvas.width/2, canvas.height/2, 10 + i * 8, 0, Math.PI * 2);
            ctx.stroke();
          }
        } else {
          // Draw iris pattern
          ctx.strokeStyle = '#8b5cf6';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(canvas.width/2, canvas.height/2, 60, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(canvas.width/2, canvas.height/2, 25, 0, Math.PI * 2);
          ctx.fillStyle = '#000';
          ctx.fill();
          // Iris texture
          for (let i = 0; i < 24; i++) {
            const angle = (i / 24) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(
              canvas.width/2 + Math.cos(angle) * 30,
              canvas.height/2 + Math.sin(angle) * 30
            );
            ctx.lineTo(
              canvas.width/2 + Math.cos(angle) * 55,
              canvas.height/2 + Math.sin(angle) * 55
            );
            ctx.strokeStyle = '#6366f1';
            ctx.stroke();
          }
        }
        
        scanY += 3;
        if (scanY > canvas.height) scanY = 0;
        
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animate();
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [capturing, captureType]);

  const startCapture = () => {
    if (deviceStatus !== 'ready') {
      toast.error(
        language === 'fr' 
          ? 'Appareil non prêt' 
          : 'Device not ready'
      );
      return;
    }
    
    setCapturing(true);
    setProgress(0);
    setError(null);
    
    // Simulate capture process
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setCapturing(false);
          
          if (captureType === 'fingerprint') {
            // Mark current finger as captured
            setCapturedFingers(prev => ({
              ...prev,
              [FINGERS[currentFinger].id]: {
                captured: true,
                timestamp: new Date().toISOString(),
                quality: Math.floor(Math.random() * 20) + 80 // 80-100 quality
              }
            }));
            
            // Move to next finger
            if (currentFinger < FINGERS.length - 1) {
              setCurrentFinger(prev => prev + 1);
            }
            
            toast.success(
              language === 'fr'
                ? `${FINGERS[currentFinger].name.fr} capturé`
                : `${FINGERS[currentFinger].name.en} captured`
            );
          } else {
            setRetinalCaptured(true);
            toast.success(
              language === 'fr'
                ? 'Scan rétinien complété'
                : 'Retinal scan completed'
            );
          }
          
          return 100;
        }
        return prev + 2;
      });
    }, 50);
  };

  const handleSave = () => {
    const data = captureType === 'fingerprint' 
      ? { fingerprints: capturedFingers, capturedAt: new Date().toISOString() }
      : { retinalScan: { captured: retinalCaptured, capturedAt: new Date().toISOString() } };
    
    onCapture(data);
    toast.success(
      language === 'fr'
        ? 'Données biométriques enregistrées'
        : 'Biometric data saved'
    );
    onClose();
  };

  const resetCapture = () => {
    setCapturedFingers({});
    setRetinalCaptured(false);
    setCurrentFinger(0);
    setProgress(0);
    setCapturing(false);
  };

  const capturedCount = Object.keys(capturedFingers).length;
  const allFingersCaptured = capturedCount === FINGERS.length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {captureType === 'fingerprint' ? (
              <>
                <Fingerprint className="w-6 h-6 text-blue-500" />
                {language === 'fr' ? 'Capture des Empreintes Digitales' : 'Fingerprint Capture'}
              </>
            ) : (
              <>
                <Eye className="w-6 h-6 text-purple-500" />
                {language === 'fr' ? 'Scan Rétinien' : 'Retinal Scan'}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {captureType === 'fingerprint'
              ? (language === 'fr' 
                  ? 'Placez chaque doigt sur le scanner pour capturer les empreintes'
                  : 'Place each finger on the scanner to capture fingerprints')
              : (language === 'fr'
                  ? 'Regardez fixement dans le scanner rétinien'
                  : 'Look directly into the retinal scanner')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Device Status */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                deviceStatus === 'ready' ? 'bg-green-500 animate-pulse' :
                deviceStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                deviceStatus === 'error' ? 'bg-red-500' : 'bg-gray-500'
              }`} />
              <span className="text-sm font-medium">
                {deviceStatus === 'ready' && (language === 'fr' ? 'Appareil prêt' : 'Device ready')}
                {deviceStatus === 'connecting' && (language === 'fr' ? 'Connexion en cours...' : 'Connecting...')}
                {deviceStatus === 'error' && (language === 'fr' ? 'Erreur de connexion' : 'Connection error')}
                {deviceStatus === 'disconnected' && (language === 'fr' ? 'Déconnecté' : 'Disconnected')}
              </span>
            </div>
            {deviceStatus === 'error' && (
              <Button variant="outline" size="sm" onClick={() => {
                setDeviceStatus('connecting');
                setTimeout(() => setDeviceStatus('ready'), 1000);
              }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Réessayer' : 'Retry'}
              </Button>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Capture Area */}
          <div className="flex gap-6">
            {/* Canvas for visualization */}
            <div className="flex-shrink-0">
              <canvas 
                ref={canvasRef}
                width={200}
                height={200}
                className="rounded-lg border-2 border-dashed border-muted-foreground/30"
              />
              {capturing && (
                <div className="mt-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-center text-muted-foreground mt-1">
                    {language === 'fr' ? 'Capture en cours...' : 'Capturing...'} {progress}%
                  </p>
                </div>
              )}
            </div>

            {/* Fingerprint Selection */}
            {captureType === 'fingerprint' && (
              <div className="flex-1 space-y-3">
                <h4 className="font-medium text-sm">
                  {language === 'fr' ? 'Doigts capturés' : 'Captured Fingers'}: {capturedCount}/10
                </h4>
                
                {/* Hand visualization */}
                <div className="grid grid-cols-5 gap-1">
                  {/* Right hand */}
                  {FINGERS.slice(0, 5).map((finger, idx) => (
                    <button
                      key={finger.id}
                      onClick={() => setCurrentFinger(idx)}
                      className={`p-2 rounded text-center text-xs transition-colors ${
                        capturedFingers[finger.id] 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : currentFinger === idx
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-2 ring-blue-500'
                            : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {capturedFingers[finger.id] ? (
                        <CheckCircle className="w-4 h-4 mx-auto" />
                      ) : (
                        <Hand className="w-4 h-4 mx-auto opacity-50" />
                      )}
                      <span className="block mt-1 truncate">{finger.name[language]?.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
                
                {/* Left hand */}
                <div className="grid grid-cols-5 gap-1">
                  {FINGERS.slice(5).map((finger, idx) => (
                    <button
                      key={finger.id}
                      onClick={() => setCurrentFinger(idx + 5)}
                      className={`p-2 rounded text-center text-xs transition-colors ${
                        capturedFingers[finger.id] 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : currentFinger === idx + 5
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-2 ring-blue-500'
                            : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {capturedFingers[finger.id] ? (
                        <CheckCircle className="w-4 h-4 mx-auto" />
                      ) : (
                        <Hand className="w-4 h-4 mx-auto opacity-50" />
                      )}
                      <span className="block mt-1 truncate">{finger.name[language]?.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>

                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? `Prochain: ${FINGERS[currentFinger].name.fr}`
                    : `Next: ${FINGERS[currentFinger].name.en}`
                  }
                </p>
              </div>
            )}

            {/* Retinal Scan Status */}
            {captureType === 'retinal' && (
              <div className="flex-1 flex flex-col items-center justify-center">
                {retinalCaptured ? (
                  <div className="text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-2" />
                    <p className="font-medium text-green-600">
                      {language === 'fr' ? 'Scan complété' : 'Scan completed'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'fr' ? 'Qualité: Excellente' : 'Quality: Excellent'}
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <ScanLine className="w-16 h-16 text-purple-500 mx-auto mb-2 opacity-50" />
                    <p className="text-muted-foreground">
                      {language === 'fr' 
                        ? 'Appuyez sur "Capturer" et regardez dans le scanner'
                        : 'Press "Capture" and look into the scanner'
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              {language === 'fr' ? 'Instructions' : 'Instructions'}
            </h4>
            <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
              {captureType === 'fingerprint' ? (
                <>
                  <li>• {language === 'fr' ? 'Assurez-vous que vos doigts sont propres et secs' : 'Make sure your fingers are clean and dry'}</li>
                  <li>• {language === 'fr' ? 'Placez le doigt à plat sur le scanner' : 'Place finger flat on the scanner'}</li>
                  <li>• {language === 'fr' ? 'Maintenez pendant la capture' : 'Hold still during capture'}</li>
                </>
              ) : (
                <>
                  <li>• {language === 'fr' ? 'Retirez vos lunettes si vous en portez' : 'Remove glasses if wearing any'}</li>
                  <li>• {language === 'fr' ? 'Regardez directement dans le scanner' : 'Look directly into the scanner'}</li>
                  <li>• {language === 'fr' ? 'Ne clignez pas des yeux pendant le scan' : 'Do not blink during the scan'}</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={resetCapture}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Réinitialiser' : 'Reset'}
          </Button>
          <Button 
            onClick={startCapture} 
            disabled={capturing || deviceStatus !== 'ready' || (captureType === 'fingerprint' && allFingersCaptured) || (captureType === 'retinal' && retinalCaptured)}
          >
            {capturing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {language === 'fr' ? 'Capture...' : 'Capturing...'}
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Capturer' : 'Capture'}
              </>
            )}
          </Button>
          <Button 
            onClick={handleSave}
            disabled={(captureType === 'fingerprint' && capturedCount === 0) || (captureType === 'retinal' && !retinalCaptured)}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Enregistrer' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BiometricCaptureModal;
